import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import styled from 'styled-components';
import { BiSearch, BiChevronDown } from 'react-icons/bi';

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 1.25rem;

  .form-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;

    .required-asterisk {
      color: #dc3545;
      margin-left: 0.25rem;
    }
  }

  .form-control {
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4b6cb7;
      box-shadow: 0 0 0 0.25rem rgba(75, 108, 183, 0.25);
    }

    &.is-invalid {
      border-color: #dc3545;
    }

    &:disabled {
      background-color: #f8f9fa;
      cursor: not-allowed;
    }
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
`;

const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResults = styled.div`
  padding: 0.75rem 1rem;
  color: #6c757d;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
`;

const educationOptions = {
  "MCA": [
    "MCA Cyber security, Ethical hacking & digital forensics",
    "MCA AIML & Data Science",
    "MCA Cloud & DevOps"
  ],
  "MSc Computer Science": [
    "MSc Cyber security, Ethical hacking & digital forensics",
    "MSc AIML & Data Science",
    "MSc Cloud & DevOps"
  ],
  "MBA": [
    "MBA Marketing, Finance & Business Analytics",
    "MBA Digital Business Management & Data Analytics"
  ]
};

const EducationInformation = ({ register, errors }) => {
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showProgramDropdown, setShowProgramDropdown] = useState(false);
  const [courseDropdownOpened, setCourseDropdownOpened] = useState(false);
  const [programDropdownOpened, setProgramDropdownOpened] = useState(false);
  const courseDropdownRef = useRef(null);
  const programDropdownRef = useRef(null);

  const { onChange: onEducationChange, ...educationField } = register('educationQualification', {
    required: 'Please select both program and specialization',
    validate: () => {
      if (!selectedCourse || !selectedProgram) {
        return 'Please select both program and specialization';
      }
      return true;
    }
  });

  // Always show all courses in dropdown regardless of search
  const filteredCourses = Object.keys(educationOptions).filter(course =>
    course.toLowerCase().includes(courseSearch.toLowerCase())
  );

  // Always show all programs in dropdown regardless of search
  const filteredPrograms = selectedCourse 
    ? educationOptions[selectedCourse].filter(program =>
        program.toLowerCase().includes(programSearch.toLowerCase())
      )
    : [];

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setCourseSearch(course);
    setProgramSearch('');
    setSelectedProgram('');
    setShowCourseDropdown(false);
    setCourseDropdownOpened(false);
    
    // Update form value
    onEducationChange({
      target: {
        name: 'educationQualification',
        value: ''
      }
    });
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    setProgramSearch(program);
    setShowProgramDropdown(false);
    setProgramDropdownOpened(false);
    
    // Update form value
    onEducationChange({
      target: {
        name: 'educationQualification',
        value: program
      }
    });
  };

  const handleCourseDropdownToggle = () => {
    const newState = !showCourseDropdown;
    setShowCourseDropdown(newState);
    
    if (newState) {
      // When opening dropdown, keep the selected value but show all options
      setCourseDropdownOpened(true);
    } else {
      setCourseDropdownOpened(false);
    }
  };

  const handleProgramDropdownToggle = () => {
    if (!selectedCourse) return;
    
    const newState = !showProgramDropdown;
    setShowProgramDropdown(newState);
    
    if (newState) {
      // When opening dropdown, keep the selected value but show all options
      setProgramDropdownOpened(true);
    } else {
      setProgramDropdownOpened(false);
    }
  };

  const handleCourseFocus = () => {
    setShowCourseDropdown(true);
    // Keep the selected value in the input field
  };

  const handleProgramFocus = () => {
    if (selectedCourse) {
      setShowProgramDropdown(true);
      // Keep the selected value in the input field
    }
  };

  const handleCourseInputChange = (e) => {
    setCourseSearch(e.target.value);
    setShowCourseDropdown(true);
    // Don't clear the selected course when typing
  };

  const handleProgramInputChange = (e) => {
    setProgramSearch(e.target.value);
    setShowProgramDropdown(true);
    // Don't clear the selected program when typing
  };

  const handleClickOutside = (event) => {
    if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
      setShowCourseDropdown(false);
      setCourseDropdownOpened(false);
    }
    if (programDropdownRef.current && !programDropdownRef.current.contains(event.target)) {
      setShowProgramDropdown(false);
      setProgramDropdownOpened(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <h5 className="section-title">Education</h5>
      <Row>
        <StyledFormGroup as={Col} md="6" controlId="courseOpted">
          <Form.Label>
            Program Opted<span className="required-asterisk">*</span>
          </Form.Label>
          <div style={{ position: 'relative' }} ref={courseDropdownRef}>
            <InputGroup>
              <Form.Control
                type="text"
                value={courseSearch}
                onChange={handleCourseInputChange}
                onFocus={handleCourseFocus}
                placeholder="Search or select program"
                isInvalid={!!errors.educationQualification && !selectedCourse}
                autoComplete="off"
              />
              <InputGroup.Text 
                style={{ cursor: 'pointer' }}
                onClick={handleCourseDropdownToggle}
              >
                <BiChevronDown />
              </InputGroup.Text>
            </InputGroup>
            
            {showCourseDropdown && (
              <DropdownContainer>
                {Object.keys(educationOptions).length > 0 ? (
                  Object.keys(educationOptions).map((course) => (
                    <DropdownItem
                      key={course}
                      onClick={() => handleCourseSelect(course)}
                      style={{
                        backgroundColor: course === selectedCourse ? '#f8f9fa' : 'transparent',
                        fontWeight: course === selectedCourse ? '500' : 'normal'
                      }}
                    >
                      {course}
                    </DropdownItem>
                  ))
                ) : (
                  <NoResults>
                    No programs found
                  </NoResults>
                )}
              </DropdownContainer>
            )}
            
            {errors.educationQualification && !selectedCourse && (
              <ErrorMessage>
                <i className="bi bi-exclamation-circle me-2"></i>
                {errors.educationQualification.message}
              </ErrorMessage>
            )}
          </div>
        </StyledFormGroup>

        <StyledFormGroup as={Col} md="6" controlId="program">
          <Form.Label>
            Specialization<span className="required-asterisk">*</span>
          </Form.Label>
          <div style={{ position: 'relative' }} ref={programDropdownRef}>
            <InputGroup>
              <Form.Control
                type="text"
                value={programSearch}
                onChange={handleProgramInputChange}
                onFocus={handleProgramFocus}
                placeholder={selectedCourse ? "Search or select specialization" : "Select a program first"}
                isInvalid={!!errors.educationQualification && selectedCourse && !selectedProgram}
                disabled={!selectedCourse}
                autoComplete="off"
              />
              <InputGroup.Text 
                style={{ cursor: selectedCourse ? 'pointer' : 'not-allowed' }}
                onClick={handleProgramDropdownToggle}
              >
                <BiChevronDown />
              </InputGroup.Text>
            </InputGroup>
            
            {showProgramDropdown && selectedCourse && (
              <DropdownContainer>
                {educationOptions[selectedCourse].length > 0 ? (
                  educationOptions[selectedCourse].map((program) => (
                    <DropdownItem
                      key={program}
                      onClick={() => handleProgramSelect(program)}
                      style={{
                        backgroundColor: program === selectedProgram ? '#e9ecef' : 'transparent',
                        fontWeight: program === selectedProgram ? '600' : 'normal'
                      }}
                    >
                      {program}
                    </DropdownItem>
                  ))
                ) : (
                  <NoResults>
                    No specializations found
                  </NoResults>
                )}
              </DropdownContainer>
            )}
            
            {errors.educationQualification && selectedCourse && !selectedProgram && (
              <ErrorMessage>
                <i className="bi bi-exclamation-circle me-2"></i>
                {errors.educationQualification.message}
              </ErrorMessage>
            )}
          </div>
        </StyledFormGroup>

        <input
          type="hidden"
          {...educationField}
          value={selectedProgram || ''}
        />
      </Row>
    </>
  );
};

export default EducationInformation;